import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { BellRing, Send } from "lucide-react";
import { toast } from "sonner";
import { pushSupported, getPushState, enablePush, disablePush, sendTestPush } from "@/lib/push";

export default function PushToggle() {
  const [state, setState] = useState({ supported: true, subscribed: false, permission: "default" });
  const [busy, setBusy] = useState(false);

  const refresh = async () => setState(await getPushState());
  useEffect(() => { refresh(); }, []);

  const onToggle = async (checked) => {
    setBusy(true);
    try {
      if (checked) {
        await enablePush();
        toast.success("Notifiche push attivate");
      } else {
        await disablePush();
        toast.success("Notifiche push disattivate");
      }
      await refresh();
    } catch (err) {
      toast.error(err.message || "Impossibile aggiornare le notifiche push");
      await refresh();
    } finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    try { await sendTestPush(); toast.success("Notifica di prova inviata"); }
    catch { toast.error("Invio non riuscito"); }
    finally { setBusy(false); }
  };

  const supported = pushSupported() && state.supported;

  return (
    <Card className="p-4 mb-6" data-testid="push-toggle">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3 min-w-0">
          <span className="h-9 w-9 rounded-xl bg-primary/10 grid place-items-center shrink-0"><BellRing className="h-5 w-5 text-primary" /></span>
          <div className="min-w-0">
            <div className="font-semibold text-sm">Notifiche push sul telefono</div>
            <p className="text-sm text-muted-foreground mt-0.5">
              Ricevi promemoria (peso, acqua, analisi) e novità della dieta anche ad app chiusa.
            </p>
            {!supported && (
              <p className="text-xs text-amber-600 mt-1" data-testid="push-unsupported">
                Su iPhone installa prima l'app sulla schermata Home (Condividi → Aggiungi a Home) per abilitare le push.
              </p>
            )}
            {state.permission === "denied" && supported && (
              <p className="text-xs text-destructive mt-1">Permesso negato: abilitalo dalle impostazioni del browser.</p>
            )}
          </div>
        </div>
        <Switch
          checked={state.subscribed}
          disabled={busy || !supported || state.permission === "denied"}
          onCheckedChange={onToggle}
          data-testid="push-switch"
        />
      </div>
      {state.subscribed && (
        <div className="mt-3 pl-12">
          <Button variant="outline" size="sm" onClick={test} disabled={busy} data-testid="push-test-button">
            <Send className="h-4 w-4 mr-2" /> Invia notifica di prova
          </Button>
        </div>
      )}
    </Card>
  );
}
